import RouteNames from '@/configuration/router/names.routes';
import { AuthMethods } from '@/models/auth-methods';
import { StorageKeys } from '@/models/storage-keys';
import authService from '@/services/AuthService';
import httpService from '@/services/HttpService';
import navigationService from '@/services/NavigationService';
import { useAuthStore } from '@/stores/authStore';
import { useUserSettingsStore } from '@/stores/userSettingsStore';
import { StorageUtils } from '@/utils/storage.utils';
import { ref } from 'vue';

/**
 * Composable for handling authentication flow logic across views
 * Provides reusable functions for:
 * - Configuring user post-authentication
 * - Navigation after authentication
 * - French authentication handling
 * - Spanish authentication handling
 */
export function useAuthFlow() {
  const authStore = useAuthStore();
  const loading = ref(false);
  const userSettingsStore = useUserSettingsStore();

  /**
   * Configures user after successful authentication
   * - For Spain: Configure products
   * - For all methods: Configure ragIndex and retrieve conversations
   */
  const configureAuthenticatedUser = async (): Promise<void> => {
    try {
      loading.value = true;

      if (authStore.authMethod === AuthMethods.Spain) {
        await configureSpanishUserProducts();
      } else {
        await checkSelectedRagIndex();
      }
    } finally {
      loading.value = false;
    }
  };

  /**
   * Configures Spanish user products and checks RAG index
   */
  const configureSpanishUserProducts = async (): Promise<void> => {
    const response = await authService.getSpainUserProducts();
    authStore.setProducts(response);
    authStore.setIsConcurrentUser(response.IsConcurrentUser ?? false);

    await checkSelectedRagIndex();
  };

  /**
   * Checks and sets the selected RAG index if needed
   */
  const checkSelectedRagIndex = async (): Promise<void> => {
    await userSettingsStore.initializeData();
    const products = authStore.products;
    if (
      (!userSettingsStore.selectedRagIndex ||
        !products.availableIndexes.includes(
          userSettingsStore.selectedRagIndex
        )) &&
      !!products.availableIndexes?.length
    )
      userSettingsStore.setSelectedRagIndex(products.availableIndexes[0]);
  };

  /**
   * Handles navigation after authentication
   * - Uses stored navigation history if available
   * - Otherwise navigates to home
   */
  const navigateAfterAuthentication = async (): Promise<void> => {
    /* const navHistory = await StorageUtils.getStorageItem(
      StorageKeys.NAV_HISTORY
    );

    if (navHistory) {
      navigationService.navigateToStoredRoute(navHistory);
    } else {
      navigationService.navigateTo({ name: RouteNames.Home });
    } */
    navigationService.navigateTo({ name: RouteNames.Home });
  };

  /**
   * Processes French authentication with username and password
   * @param username User's username
   * @param password User's password
   * @returns Promise resolving to true if authenticated
   */
  const processFrenchAuth = async (
    username: string,
    password: string
  ): Promise<boolean> => {
    try {
      loading.value = true;
      const response = await authService.login(username, password);

      if (response?.token) {
        authStore.setToken(response.token);
        await configureAuthenticatedUser();
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error during French authentication:', error);
      throw error;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Processes Spanish authentication
   * @returns Promise resolving to true if authenticated
   */
  const processSpanishAuth = async (): Promise<boolean> => {
    try {
      loading.value = true;
      const isAuthenticated = await checkSpanishAuth();

      if (isAuthenticated) {
        await configureAuthenticatedUser();
        return true;
      }

      return false;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Checks current authentication status and configures user if needed
   * @returns Promise resolving to true if authenticated
   */
  const checkCurrentAuth = async (): Promise<boolean> => {
    try {
      loading.value = true;
      let isAuthenticated = false;

      switch (authStore.authMethod) {
        case AuthMethods.Spain:
          isAuthenticated = await checkSpanishAuth();
          break;
        case AuthMethods.FranceMock:
          isAuthenticated = await checkFrenchToken();
          break;
        default:
          isAuthenticated = false;
      }

      if (isAuthenticated) {
        await configureAuthenticatedUser();
      }

      return isAuthenticated;
    } catch (error) {
      console.error('Error checking authentication:', error);
      return false;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Helper function to check French token and set HTTP authorization header
   */
  const checkFrenchToken = async (): Promise<boolean> => {
    try {
      const token = await StorageUtils.getStorageItem(StorageKeys.USER_TOKEN);
      if (token) {
        httpService.setAuthorizationHeader(token);
        authStore.setToken(token);
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error checking French token:', err);
      return false;
    }
  };

  /**
   * Checks if the user is authenticated in Spain
   * - Returns true if authenticated, false otherwise
   */
  const checkSpanishAuth = async (): Promise<boolean> => {
    try {
      const authResponse = await authService.isAuthenticatedInSpain();
      return authResponse;
    } catch (err) {
      console.error('Error checking Spain auth:', err);
      return false;
    }
  };
  return {
    loading,
    configureAuthenticatedUser,
    navigateAfterAuthentication,
    processFrenchAuth,
    processSpanishAuth,
    checkCurrentAuth,
    configureSpanishUserProducts,
    checkSelectedRagIndex
  };
}
